/**
 * MQTT presenter client — retained `state`, inbound `command` (+ optional `emote`).
 */

import { ensureMqttGlobal, getMqttNamespace, type MqttClientLike } from '../../remote-mqtt-shared';
import {
  buildParticipantStatusEnvelope,
  buildStateEnvelope,
  isRemoteV1WireEnvelope,
  parseRemoteV1CommandPayload,
  parseRemoteV1EmotePayload,
  REMOTE_V1_CLIENT_VERSION,
  REMOTE_V1_STATUS_HEARTBEAT_MS,
  topicParticipantStatus,
  topicSessionCommand,
  topicSessionEmote,
  topicSessionState,
  type RemoteV1EmotePayload,
  type RemoteV1ParticipantConnectionStatus,
  type RemoteV1StatePayload,
  type RemoteV1WireEnvelope
} from './protocol';
import { remoteTtsVerbose, remoteTtsWarn } from '../../../lib/remote-tts-log';
import { applyRemoteV1CommandToReveal, buildRemoteV1SessionEndedState } from './reveal-bridge';

export interface RemoteV1EmoteInboundMeta {
  participantId?: string;
}

export interface RemoteMqttPresenterV1Hooks {
  getState: () => RemoteV1StatePayload | null;
  onCommandApplied?: () => void;
  /** Called after a state payload is published to MQTT (same object as in the envelope). */
  onStatePublished?: (state: RemoteV1StatePayload) => void;
  onEmote?: (payload: RemoteV1EmotePayload, meta: RemoteV1EmoteInboundMeta) => void;
  onStatus?: (status: 'connecting' | 'connected' | 'disconnected' | 'error') => void;
  onDebug?: (message: string, data?: Record<string, unknown>) => void;
}

export interface RemoteMqttPresenterV1Service {
  start: () => Promise<void>;
  stop: () => void;
  publishState: () => void;
  /** Publish arbitrary retained state (e.g. `mode: 'ended'`) without reading `getState`. */
  publishStatePayload: (state: RemoteV1StatePayload) => void;
  getStateTopic: () => string;
  getCommandTopic: () => string;
}

export function createRemoteMqttPresenterV1Service(
  mqttUrl: string,
  ctx: {
    presentationId: string;
    sessionId: string;
    participantId: string;
    commandToken?: string;
    mqttAuth?: { username: string; password: string } | null;
  },
  hooks: RemoteMqttPresenterV1Hooks
): RemoteMqttPresenterV1Service {
  const stateTopic = topicSessionState(ctx.presentationId, ctx.sessionId);
  const commandTopic = topicSessionCommand(ctx.presentationId, ctx.sessionId);
  const emoteTopic = topicSessionEmote(ctx.presentationId, ctx.sessionId);
  const statusTopic = topicParticipantStatus(ctx.presentationId, ctx.sessionId, ctx.participantId);
  let client: MqttClientLike | null = null;
  let started = false;
  let statusHeartbeatTimer: number | null = null;

  function clearStatusHeartbeat(): void {
    if (statusHeartbeatTimer != null) {
      window.clearInterval(statusHeartbeatTimer);
      statusHeartbeatTimer = null;
    }
  }

  function startStatusHeartbeat(): void {
    clearStatusHeartbeat();
    statusHeartbeatTimer = window.setInterval(() => {
      if (client?.connected) {
        client.publish(statusTopic, participantStatusJson('online'), { qos: 0, retain: true });
      }
    }, REMOTE_V1_STATUS_HEARTBEAT_MS);
  }

  function participantStatusJson(status: RemoteV1ParticipantConnectionStatus): string {
    return JSON.stringify(
      buildParticipantStatusEnvelope({
        sessionId: ctx.sessionId,
        presentationId: ctx.presentationId,
        participantId: ctx.participantId,
        role: 'presenter',
        payload: {
          participant_id: ctx.participantId,
          role: 'presenter',
          status,
          client: { kind: 'lectura-next-presenter', version: REMOTE_V1_CLIENT_VERSION }
        }
      })
    );
  }

  function debug(message: string, data?: Record<string, unknown>): void {
    hooks.onDebug?.(message, data);
  }

  function publishJson(topic: string, obj: unknown, retain?: boolean): void {
    if (!client?.connected) return;
    client.publish(topic, JSON.stringify(obj), { retain: !!retain });
  }

  function publishStatePayload(state: RemoteV1StatePayload): void {
    const env = buildStateEnvelope({
      sessionId: ctx.sessionId,
      presentationId: ctx.presentationId,
      participantId: ctx.participantId,
      role: 'presenter',
      state
    });
    publishJson(stateTopic, env, true);
    hooks.onStatePublished?.(state);
  }

  function publishState(): void {
    const state = hooks.getState();
    if (!state) return;
    publishStatePayload(state);
  }

  function handleIncoming(topic: string, payload: unknown): void {
    if (topic !== commandTopic && topic !== emoteTopic) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(typeof payload === 'string' ? payload : String(payload || ''));
    } catch (e) {
      if (topic === emoteTopic) {
        console.warn('[Lectura][emote] presenter: MQTT emote payload not JSON', { error: String(e) });
      }
      debug('invalid-json', { error: String(e) });
      return;
    }
    if (!isRemoteV1WireEnvelope(parsed)) {
      if (topic === emoteTopic) {
        console.warn('[Lectura][emote] presenter: MQTT emote not a valid v1 envelope', { parsed });
      }
      debug('invalid-envelope');
      return;
    }
    const env = parsed as RemoteV1WireEnvelope;
    if (env.session_id !== ctx.sessionId || env.presentation_id !== ctx.presentationId) {
      if (topic === emoteTopic && env.t === 'emote') {
        console.warn('[Lectura][emote] presenter: emote dropped (session/presentation id mismatch)', {
          expected: { session_id: ctx.sessionId, presentation_id: ctx.presentationId },
          envelope: { session_id: env.session_id, presentation_id: env.presentation_id }
        });
      }
      return;
    }

    if (env.t === 'emote') {
      const em = parseRemoteV1EmotePayload(env.payload);
      if (!em) {
        console.warn('[Lectura][emote] presenter: could not parse emote payload', { payload: env.payload });
        return;
      }
      const actorId = env.actor?.participant_id;
      if (actorId && actorId === ctx.participantId) {
        return;
      }
      debug('audience-emote', { emoji: em.emoji, scale: em.scale, actorId: actorId ?? '' });
      hooks.onEmote?.(em, { participantId: actorId });
      return;
    }
    if (env.t !== 'command') return;
    if (ctx.commandToken && env.command_token !== ctx.commandToken) {
      debug('rejected-command-token');
      const p = env.payload;
      const isTts =
        p &&
        typeof p === 'object' &&
        (p as { type?: string }).type === 'TTS_RUN';
      if (isTts) {
        remoteTtsWarn('presenter: command dropped (command_token mismatch)');
      }
      return;
    }
    const cmd = parseRemoteV1CommandPayload(env.payload);
    if (!cmd) {
      debug('invalid-command-payload');
      const p = env.payload;
      const isTtsInvalid =
        p &&
        typeof p === 'object' &&
        (p as { type?: string }).type === 'TTS_RUN';
      if (isTtsInvalid) {
        remoteTtsWarn('presenter: invalid command payload', { payload: env.payload });
      }
      return;
    }
    if (cmd.type === 'TTS_RUN') {
      remoteTtsVerbose('presenter MQTT: received TTS_RUN', {
        codeword: String(cmd.codeword || '').slice(0, 96)
      });
    }
    applyRemoteV1CommandToReveal(cmd);
    hooks.onCommandApplied?.();
  }

  async function start(): Promise<void> {
    if (started) return;
    started = true;
    hooks.onStatus?.('connecting');
    await ensureMqttGlobal();
    const mqtt = getMqttNamespace();
    if (!mqtt) {
      hooks.onStatus?.('error');
      debug('mqtt-unavailable');
      return;
    }
    const willEndedState = buildRemoteV1SessionEndedState(null);
    const willPayload = JSON.stringify(
      buildStateEnvelope({
        sessionId: ctx.sessionId,
        presentationId: ctx.presentationId,
        participantId: ctx.participantId,
        role: 'presenter',
        state: willEndedState
      })
    );
    const opts: Record<string, unknown> = {
      connectTimeout: 12000,
      reconnectPeriod: 3000,
      keepalive: 30,
      /** Unclean disconnect / keep-alive timeout: retain “ended” on `state` so remotes update. */
      will: {
        topic: stateTopic,
        payload: willPayload,
        qos: 0,
        retain: true
      }
    };
    if (ctx.mqttAuth?.username) {
      opts.username = ctx.mqttAuth.username;
      opts.password = ctx.mqttAuth.password || '';
    }
    client = mqtt.connect(mqttUrl, opts);
    client.on('connect', () => {
      hooks.onStatus?.('connected');
      debug('connected', { stateTopic, commandTopic });
      client?.subscribe(commandTopic);
      client?.subscribe(emoteTopic);
      client?.publish(statusTopic, participantStatusJson('online'), { qos: 0, retain: true });
      publishState();
    });
    client.on('close', () => {
      hooks.onStatus?.('disconnected');
      debug('disconnected');
    });
    client.on('error', (err: unknown) => {
      hooks.onStatus?.('error');
      debug('error', { error: String(err) });
    });
    client.on('message', (t: unknown, buf: unknown) => {
      handleIncoming(String(t), buf as Buffer);
    });
  }

  function stop(): void {
    if (!started && !client) return;
    started = false;
    clearStatusHeartbeat();
    const c = client;
    client = null;
    if (c?.connected) {
      const msg = participantStatusJson('offline');
      c.publish(statusTopic, msg, { qos: 0, retain: true }, () => {
        try {
          c.end?.(true);
        } catch {
          /* ignore */
        }
      });
      window.setTimeout(() => {
        try {
          c.end?.(true);
        } catch {
          /* ignore */
        }
      }, 500);
    } else {
      c?.end?.(true);
    }
  }

  return {
    start,
    stop,
    publishState,
    publishStatePayload,
    getStateTopic: () => stateTopic,
    getCommandTopic: () => commandTopic
  };
}
